import _isNil from 'lodash/isNil';
import {
    MapLayerMouseEvent,
    MapboxGeoJSONFeature,
    PointLike,
} from 'react-map-gl';
import { IFolder } from '@Data/folders/Interfaces';
import GeoserverApi from '@Data/geoserver/Api';
import { IGeoserverFeature } from '@Data/geoserver/Interfaces';
import { AxiosClient } from '@Services/data/AxiosClient';
import Styles from './MapComponents.scss';

export function getBoundingBoxFromMouseEvent(
    e: MapLayerMouseEvent,
    radius?: number
): [PointLike, PointLike] {
    return extendBoundingBox(
        [
            [e.point.x, e.point.y],
            [e.point.x, e.point.y],
        ],
        radius
    );
}

export function extendBoundingBox(bbox, radius): [PointLike, PointLike] {
    const extension = radius ? radius : 2.5;
    return [
        [bbox[0][0] - extension, bbox[0][1] - extension],
        [bbox[1][0] + extension, bbox[1][1] + extension],
    ];
}

export function mapMapboxFeature(f: MapboxGeoJSONFeature): IMapboxFeature {
    return {
        type: f.type,
        layer: { ...f.layer },
        properties: { ...f.properties },
        geometry: { ...f.geometry },
    };
}

export function queryRenderedFeatures(mapRef, boundaryBox?, queryProps?) {
    return mapRef.current
        .queryRenderedFeatures(boundaryBox, queryProps)
        .map((f) => mapMapboxFeature(f));
}

export const getTileUrl = async (folderId) => {
    return await GeoserverApi.getVectorTiles(folderId);
};

export const getWpsString = (
    wpIds: Array<string>,
    encode?: boolean
): string => {
    if (encode) {
        wpIds = wpIds.map((wpid) => `'${wpid}'`);
        return wpIds.join('\\,');
    } else {
        return wpIds.join();
    }
};

export const getWfsData = async (
    folderIds: Array<string>,
    projectId: number
) => {
    return await GeoserverApi.getWfsData(folderIds, projectId);
};

export const getMapMenuWidthInPixels = (pixelStr: string): number => {
    return parseFloat(pixelStr.split('px')[0]);
};

export const parseClientId = (clientId: string): string => {
    return clientId.replace(/[{}]/g, '');
};

export const mapWfsData = (
    features: Array<IGeoserverFeature>
): Array<IGeoserverFeature> => {
    return features.map((f, i) => {
        const clientId = parseClientId(f.properties.client_id);
        const processResults = JSON.parse(f.properties.process_results);
        const latestPoletagResult = processResults.find(
            (p) => p.process_name === 'pole tagger v2'
        );

        let colour = 'black';
        if (
            !_isNil(latestPoletagResult) &&
            !_isNil(latestPoletagResult.result)
        ) {
            const temp = latestPoletagResult.result.toLowerCase();
            switch (temp) {
                case 'blue':
                    colour = Styles.entityBlue;
                    break;
                case 'green':
                    colour = Styles.entityGreen;
                    break;
                case 'orange':
                    colour = Styles.entityOrange;
                    break;
                default:
                    colour = temp;
                    break;
            }
        }

        return {
            ...f,
            properties: {
                ...f.properties,
                index: i,
                colour: colour,
                client_id: clientId,
            },
        };
    });
};

export interface IPopupDetails {
    coordinates: [number, number];
    entityVersionsByClientId: any;
}

export interface IMapboxFeature {
    type: any;
    layer: any;
    properties: any;
    geometry: any;
}

export enum LayerType {
    POINT,
    CLUSTER,
    CLUSTER_NUMBER,
    SELECTED_ENTITY,
}

export function getLayerId(baseLayerId: string, layerType: LayerType) {
    switch (layerType) {
        case LayerType.POINT:
            return `points-${baseLayerId}`;
        case LayerType.CLUSTER:
            return `clusters-${baseLayerId}`;
        case LayerType.CLUSTER_NUMBER:
            return `cluster-numbers-${baseLayerId}`;
        case LayerType.SELECTED_ENTITY:
            return `selected-entity-${baseLayerId}`;
    }
}
